// Accessibility (Maxsus imkoniyatlar) — shared state + side effects.
//
// One reactive settings object drives everything; changes are applied to the
// #app element (vision filters + page zoom), wired to a click-to-read screen
// reader, and persisted to localStorage so they survive navigation/reload.
// The panel itself is teleported to <body> (outside #app), so filters/zoom
// never distort the panel.
import { reactive, watch } from 'vue'

const KEY = 'trm_a11y'

const DEFAULTS = {
  mode: 'default',   // default | invert | contrast | gray
  fontScale: 1,      // 1 – 1.6
  zoom: 1,           // 1 – 1.6
  reader: false,     // click-to-read screen reader
  voice: 'male',     // male | female
  reading: false     // reading mode (extra line spacing)
}

function load() {
  try {
    return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(KEY)) || {}) }
  } catch {
    return { ...DEFAULTS }
  }
}

export const ui = reactive({ open: false })
export const settings = reactive(load())

function persist() {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...settings }))
  } catch { /* ignore quota / privacy mode */ }
}

export function apply() {
  const app = document.getElementById('app')
  if (!app) return
  app.classList.toggle('a11y-invert', settings.mode === 'invert')
  app.classList.toggle('a11y-contrast', settings.mode === 'contrast')
  app.classList.toggle('a11y-gray', settings.mode === 'gray')
  app.classList.toggle('a11y-reading', settings.reading)
  const z = settings.fontScale * settings.zoom
  app.style.zoom = Math.abs(z - 1) < 0.001 ? '' : String(Number(z.toFixed(3)))
}

export function resetSettings() {
  Object.assign(settings, DEFAULTS)
}

// ── Screen reader (Web Speech API) ──────────────────────────────────
export function speak(text) {
  if (!('speechSynthesis' in window) || !text) return
  window.speechSynthesis.cancel()
  const u = new SpeechSynthesisUtterance(text)
  const voices = window.speechSynthesis.getVoices()
  const pick =
    voices.find((v) => /uz/i.test(v.lang)) ||
    voices.find((v) => /ru/i.test(v.lang)) ||
    voices[0]
  if (pick) {
    u.voice = pick
    u.lang = pick.lang
  }
  // Approximate gender via pitch when no gendered voice is available.
  u.pitch = settings.voice === 'female' ? 1.25 : 0.8
  u.rate = 0.98
  window.speechSynthesis.speak(u)
}

function onReaderClick(e) {
  // Never read (or hijack clicks on) the panel itself.
  if (e.target.closest('.a11y-panel, .a11y-overlay')) return
  const el = e.target.closest('p,h1,h2,h3,h4,h5,h6,a,li,span,button,label,td,th')
  if (!el) return
  const text = (el.innerText || el.textContent || '').trim()
  if (text) speak(text)
}

let readerBound = false
function syncReader() {
  if (settings.reader && !readerBound) {
    document.addEventListener('click', onReaderClick, true)
    readerBound = true
  } else if (!settings.reader && readerBound) {
    document.removeEventListener('click', onReaderClick, true)
    readerBound = false
    if ('speechSynthesis' in window) window.speechSynthesis.cancel()
  }
}

export function initAccessibility() {
  apply()
  syncReader()
  watch(
    settings,
    () => {
      apply()
      syncReader()
      persist()
    },
    { deep: true }
  )
}
